import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getFormat, parseData } from '../helpers.js';
import { confirm } from '../prompt.js';
import { requireActiveOrg } from '../require-active-org.js';

export const databasesCommand = new Command('bases')
  .description('Manage bases');

databasesCommand.command('list')
  .description('List all bases')
  .option('--tag <tag>', 'Filter by tag')
  .action(async function (this: Command, opts: { tag?: string }) {
    const client = new ApiClient();
    const qs = opts.tag ? `?tag=${encodeURIComponent(opts.tag)}` : '';
    const data = await client.request('GET', `/v1/bases${qs}`);
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('get <id>')
  .description('Get a database')
  .action(async function (this: Command, id: string) {
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/bases/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('create <id>')
  .description('Create a database')
  .requiredOption('--name <name>', 'Base name')
  .option('--description <desc>', 'Description')
  .option('--tags <tags>', 'Comma-separated tags (e.g., client:acme,billing)')
  .option('--timezone <tz>', 'Default IANA timezone for data.* datetime canonicalization (e.g. America/Sao_Paulo); a record_type\'s own x-timezone overrides it')
  .action(async function (this: Command, id: string, opts: { name: string; description?: string; tags?: string; timezone?: string }) {
    await requireActiveOrg();
    const client = new ApiClient();
    const body: Record<string, unknown> = {
      name: opts.name,
      description: opts.description,
    };
    if (opts.tags) {
      body['tags'] = opts.tags.split(',').map(t => t.trim());
    }
    if (opts.timezone) {
      body['settings'] = { timezone: opts.timezone };
    }
    const data = await client.request('PUT', `/v1/bases/${id}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('update <id>')
  .description('Update a database\'s mutable config (display name, description, tags, default timezone/settings). The id/slug is immutable.')
  .option('--name <name>', 'New display name')
  .option('--description <desc>', 'Description')
  .option('--tags <tags>', 'Comma-separated tags (e.g., client:acme,billing) — replaces the existing tags')
  .option('--timezone <tz>', 'Default IANA timezone for data.* datetime canonicalization (e.g. America/Sao_Paulo); a record_type\'s own x-timezone overrides it. Merges into existing settings.')
  .option('--settings <json>', 'Raw settings JSON object (inline JSON or @file.json) — replaces the entire settings object')
  .option('--integrations <mode>', 'enabled | disabled. Preview-only: `disabled` neutralizes every external integration edge (sources, external_write triggers, inbound hydration) so the (seeded) preview behaves natively for prod-safe agent evals')
  .action(async function (
    this: Command,
    id: string,
    opts: { name?: string; description?: string; tags?: string; timezone?: string; settings?: string; integrations?: string },
  ) {
    if (
      opts.name === undefined && opts.description === undefined && opts.tags === undefined &&
      opts.timezone === undefined && opts.settings === undefined && opts.integrations === undefined
    ) {
      throw new Error('Nothing to update. Provide at least one of --name, --description, --tags, --timezone, --settings, --integrations.');
    }
    if (opts.integrations !== undefined && opts.integrations !== 'enabled' && opts.integrations !== 'disabled') {
      throw new Error('--integrations must be "enabled" or "disabled".');
    }

    const client = new ApiClient();
    const body: Record<string, unknown> = {};
    if (opts.name !== undefined) body['name'] = opts.name;
    if (opts.description !== undefined) body['description'] = opts.description;
    if (opts.tags !== undefined) body['tags'] = opts.tags.split(',').map(t => t.trim());
    if (opts.integrations !== undefined) body['integrations'] = opts.integrations;

    if (opts.settings !== undefined || opts.timezone !== undefined) {
      // updateBase replaces `settings` wholesale, so build the full object. --settings is
      // the raw base; --timezone merges on top (and, when alone, into the existing settings so
      // it doesn't clobber other keys — read-modify-write via one GET).
      let settings: Record<string, unknown>;
      if (opts.settings !== undefined) {
        let parsed: Record<string, unknown>;
        try {
          parsed = parseData(opts.settings);
        } catch (e) {
          // parseData both JSON.parses and (for the @file.json form) reads the file, so
          // surface the underlying reason — a missing/unreadable file or a JSON syntax
          // error — rather than masking both as "must be a valid JSON object".
          throw new Error(`--settings: ${e instanceof Error ? e.message : 'could not be read as JSON'}`);
        }
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('--settings must be a valid JSON object');
        }
        settings = parsed;
      } else {
        const existing = await client.request<{ settings?: Record<string, unknown> }>('GET', `/v1/bases/${id}`);
        settings = { ...(existing?.settings ?? {}) };
      }
      if (opts.timezone !== undefined) settings['timezone'] = opts.timezone;
      body['settings'] = settings;
    }

    const data = await client.request('PUT', `/v1/bases/${id}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('rename <id>')
  .description('Rename a database\'s display name (the database id/slug is immutable — it scopes token grants, MCP endpoints, and references)')
  .requiredOption('--name <name>', 'New display name')
  .action(async function (this: Command, id: string, opts: { name: string }) {
    const client = new ApiClient();
    const data = await client.request('PUT', `/v1/bases/${id}`, { name: opts.name });
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('tag <id>')
  .description('Set tags on a database')
  .requiredOption('--tags <tags>', 'Comma-separated tags (e.g., client:acme,billing)')
  .action(async function (this: Command, id: string, opts: { tags: string }) {
    const client = new ApiClient();
    const data = await client.request('PUT', `/v1/bases/${id}`, {
      tags: opts.tags.split(',').map(t => t.trim()),
    });
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('delete <id>')
  .description('Delete a database')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async (id: string, opts: { yes?: boolean }) => {
    if (!opts.yes && !(await confirm(`Delete database "${id}"? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const client = new ApiClient();
    await client.request('DELETE', `/v1/bases/${id}`);
    console.log('Deleted');
  });

// --- Environment commands ---

databasesCommand.command('create-preview <production-id>')
  .description('Create a preview database linked to a production database')
  .requiredOption('--name <name>', 'Preview database name')
  .option('--description <desc>', 'Description')
  .option('--integrations <mode>', 'enabled (default) | disabled. `disabled` makes this preview a seeded, prod-safe agent-eval target: every external integration edge is inert and it behaves natively')
  .action(async function (this: Command, productionId: string, opts: { name: string; description?: string; integrations?: string }) {
    if (opts.integrations !== undefined && opts.integrations !== 'enabled' && opts.integrations !== 'disabled') {
      throw new Error('--integrations must be "enabled" or "disabled".');
    }
    const client = new ApiClient();
    const data = await client.request('POST', `/v1/${productionId}/preview`, {
      name: opts.name,
      description: opts.description,
      ...(opts.integrations !== undefined ? { integrations: opts.integrations } : {}),
    });
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('list-previews <production-id>')
  .description('List preview bases for a production database')
  .action(async function (this: Command, productionId: string) {
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${productionId}/previews`);
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('promote <production-id>')
  .description('Promote config from a preview database to production (human-only)')
  .requiredOption('--from <preview-id>', 'Source preview database ID')
  .option('--dry-run', 'Show what would change without applying')
  .option('--record_types <ids>', 'Comma-separated record_type IDs to promote', (v: string) => v.split(','))
  .option('--triggers <ids>', 'Comma-separated trigger IDs to promote', (v: string) => v.split(','))
  .option('--inbound-webhooks <ids>', 'Comma-separated inbound webhook IDs to promote', (v: string) => v.split(','))
  .action(async function (
    this: Command,
    productionId: string,
    opts: { from: string; dryRun?: boolean; record_types?: string[]; triggers?: string[]; inboundWebhooks?: string[] },
  ) {
    const client = new ApiClient();
    const data = await client.request<{ source_credential_warnings?: { record_type_id: string; source_name: string; message: string }[] }>(
      'POST',
      `/v1/${productionId}/promote`,
      {
        source_base_id: opts.from,
        dry_run: opts.dryRun ?? false,
        record_types: opts.record_types,
        triggers: opts.triggers,
        inbound_webhooks: opts.inboundWebhooks,
      },
    );
    console.log(formatOutput(data, getFormat(this)));
    // Advisory: a net-new inline source credential (on a new record_type, or a new source on a
    // modified one) is never copied across environments — it must be set on production directly.
    // Printed to stderr so machine-readable stdout stays clean.
    const warnings = data?.source_credential_warnings ?? [];
    if (warnings.length > 0) {
      console.warn('\n⚠ External source credentials are NOT carried over by promotion — set these on production before the source will work:');
      for (const w of warnings) console.warn(`  • ${w.message}`);
    }
  });

databasesCommand.command('rollback <production-id>')
  .description('Rollback a promotion (human-only)')
  .requiredOption('--promotion <promotion-id>', 'Promotion ID to rollback')
  .action(async function (this: Command, productionId: string, opts: { promotion: string }) {
    const client = new ApiClient();
    const data = await client.request('POST', `/v1/${productionId}/promote/rollback`, {
      promotion_id: opts.promotion,
    });
    console.log(formatOutput(data, getFormat(this)));
  });

databasesCommand.command('promotions <production-id>')
  .description('List promotion history for a production database')
  .action(async function (this: Command, productionId: string) {
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${productionId}/promotions`);
    console.log(formatOutput(data, getFormat(this)));
  });
